import React, { useState, useEffect } from 'react';
import { Clock, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { readResponse, readResponseJSON } from '../../utils/fetchSafe';

const LaborSummaryTab = ({ projectId }) => {
  const { user } = useAuth();
  const canEditLabor = user?.is_admin || (user?.roles || []).includes('Time Approver');
  const [laborTimeslips, setLaborTimeslips] = useState([]);
  const [laborEmpRates, setLaborEmpRates] = useState({});
  const [employees, setEmployees] = useState([]);
  const [laborTotals, setLaborTotals] = useState({ hours: 0, amount: 0, vehicle: 0, fuel: 0 });
  const [showAddRow, setShowAddRow] = useState(false);
  const [newEntry, setNewEntry] = useState({ employee: '', date: '', hours: '', vehicle: '', fuel_expense: '' });
  const [searchTerm, setSearchTerm] = useState('');

  // Normalize any incoming date string to M/D/YYYY for consistent display.
  const formatDate = (value) => {
    if (!value) return '';
    const s = String(value).trim();
    // YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss...
    const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const [, y, m, d] = isoMatch;
      return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
    }
    // M/D/YYYY or MM/DD/YYYY
    const usMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (usMatch) {
      const [, m, d, y] = usMatch;
      const year = y.length === 2 ? `20${y}` : y;
      return `${parseInt(m, 10)}/${parseInt(d, 10)}/${year}`;
    }
    // Fallback: try Date parser
    const dt = new Date(s);
    if (!isNaN(dt.getTime())) {
      return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`;
    }
    return s;
  };

  const slipAmount = (slip, rates) => {
    // Prefer the slip's STORED amount (set by the OCR importer or by manual
    // edit) — it reflects the actual rate used on that specific day, which
    // may differ from the employee's current default pay_rate (e.g. an
    // employee billed at $18/hr on one job and $25/hr on another).
    const stored = parseFloat(String(slip.amount || '0').replace(/[$,]/g, '')) || 0;
    if (stored > 0) return stored;
    const h = parseFloat(slip.hours) || 0;
    const r = parseFloat(rates[slip.employee]) || 0;
    return h * r;
  };

  const calcTotals = (slips, rates) => ({
    hours: slips.reduce((s, t) => s + (parseFloat(t.hours) || 0), 0),
    amount: slips.reduce((s, t) => s + slipAmount(t, rates), 0),
    vehicle: slips.reduce((s, t) => s + (parseFloat(String(t.vehicle || '0').replace(/[$,]/g, '')) || 0), 0),
    fuel: slips.reduce((s, t) => s + (parseFloat(String(t.fuel_expense || '0').replace(/[$,]/g, '')) || 0), 0),
  });

  // Convert a stored date value (could be "M/D/YYYY", "YYYY-MM-DD", or ISO
  // datetime) to "YYYY-MM-DD" for the HTML date input. Returns '' on parse
  // failure so the input stays blank rather than NaN-ish.
  const toISODate = (value) => {
    if (!value) return '';
    const s = String(value).trim();
    const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    const usMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (usMatch) {
      const [, m, d, y] = usMatch;
      const yr = y.length === 2 ? `20${y}` : y;
      return `${yr}-${String(parseInt(m, 10)).padStart(2, '0')}-${String(parseInt(d, 10)).padStart(2, '0')}`;
    }
    return '';
  };

  // Convert "YYYY-MM-DD" from the date input back to the "M/D/YYYY" format
  // that the rest of the system (and the legacy importer) uses for storage.
  const fromISODate = (iso) => {
    if (!iso) return '';
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return iso;
    return `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}/${m[1]}`;
  };

  useEffect(() => {
    if (!projectId) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    Promise.all([
      fetch(`${API_URL}/api/timeslips?project_number=${projectId}`).then(readResponseJSON),
      fetch(`${API_URL}/api/employees`).then(readResponseJSON),
    ]).then(([slipData, empData]) => {
      const slips = slipData.timeslips || [];
      setLaborTimeslips(slips);
      const empList = empData.employees || [];
      setEmployees(empList);
      const rateMap = {};
      empList.forEach(e => {
        const formal = `${e.last_name}, ${e.first_name}`;
        if (e.pay_rate) {
          rateMap[formal] = e.pay_rate;
          // Also index by raw `name` (used by OCR-imported timeslips like "Y.Beltrecaw").
          if (e.name) rateMap[e.name] = e.pay_rate;
        }
      });
      setLaborEmpRates(rateMap);
      setLaborTotals(calcTotals(slips, rateMap));
    }).catch(() => {});
  }, [projectId]);

  const updateLaborSlip = (idx, field, value) => {
    setLaborTimeslips(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const saveLaborChanges = async () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
    for (const slip of laborTimeslips) {
      if (slip.slip_id) {
        await fetch(`${API_URL}/api/timeslips/${slip.slip_id}`, {
          method: 'PUT', headers,
          body: JSON.stringify({
            hours: parseFloat(slip.hours) || 0,
            date: slip.date,
            vehicle: slip.vehicle,
            fuel_expense: slip.fuel_expense,
          }),
        }).catch(() => {});
      }
    }
    setLaborTotals(calcTotals(laborTimeslips, laborEmpRates));
    toast.success('Labor data saved');
  };

  const handleAddEntry = async () => {
    if (!newEntry.employee || !newEntry.date || !newEntry.hours) {
      toast.error('Please fill in Employee, Date, and Hours');
      return;
    }
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    const entry = {
      employee: newEntry.employee,
      date: newEntry.date,
      hours: parseFloat(newEntry.hours) || 0,
      vehicle: newEntry.vehicle || '$0.00',
      fuel_expense: newEntry.fuel_expense || '$0.00',
      project_number: projectId,
      customer: '', project: '', project_address: '',
      job_type: '', tech_temps: '', rate: '',
    };
    try {
      await fetch(`${API_URL}/api/timeslips`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(entry),
      });
      // Refresh timeslips
      const res = await fetch(`${API_URL}/api/timeslips?project_number=${projectId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const { data } = await readResponse(res);
      const slips = data.timeslips || [];
      setLaborTimeslips(slips);
      setLaborTotals(calcTotals(slips, laborEmpRates));
      setNewEntry({ employee: '', date: '', hours: '', vehicle: '', fuel_expense: '' });
      setShowAddRow(false);
      toast.success('Labor entry added');
    } catch {
      toast.error('Failed to add labor entry');
    }
  };

  return (
    <div className="bg-white rounded shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl text-[#5B8DB8]">Labor Summary</h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            Search:
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Filter by employee"
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-48"
              data-testid="labor-search"
            />
          </div>
          <button
            onClick={() => setShowAddRow(!showAddRow)}
            className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-4 py-1.5 rounded text-sm flex items-center gap-1 transition-colors"
            data-testid="add-labor-btn"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2">
              <th className="text-left p-3 text-gray-600 font-normal">Employee</th>
              <th className="text-left p-3 text-gray-600 font-normal">Date</th>
              <th className="text-left p-3 text-gray-600 font-normal">Hours</th>
              <th className="text-left p-3 text-gray-600 font-normal">Amount</th>
              <th className="text-left p-3 text-gray-600 font-normal">Vehicle Expense</th>
              <th className="text-left p-3 text-gray-600 font-normal">Fuel Expense</th>
              <th className="text-left p-3 text-gray-600 font-normal">Go to Timeclock</th>
            </tr>
          </thead>
          <tbody>
            {showAddRow && (
              <tr className="border-b bg-blue-50">
                <td className="p-1">
                  <select
                    value={newEntry.employee}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, employee: e.target.value }))}
                    className="border border-gray-300 rounded px-2 py-1.5 text-sm w-full"
                    data-testid="add-labor-employee"
                  >
                    <option value="">Select Employee</option>
                    {employees.map(e => (
                      <option key={e.email} value={`${e.last_name}, ${e.first_name}`}>
                        {e.last_name}, {e.first_name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="p-1">
                  <input
                    type="date"
                    value={newEntry.date}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, date: e.target.value }))}
                    className="border border-gray-300 rounded px-2 py-1.5 w-36 text-sm"
                    data-testid="add-labor-date"
                  />
                </td>
                <td className="p-1">
                  <input
                    type="text"
                    value={newEntry.hours}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, hours: e.target.value }))}
                    className="border border-gray-300 rounded px-2 py-1.5 w-16 text-sm"
                    data-testid="add-labor-hours"
                  />
                </td>
                <td className="p-2 text-gray-400 text-sm">--</td>
                <td className="p-1">
                  <input
                    type="text"
                    value={newEntry.vehicle}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, vehicle: e.target.value }))}
                    className="border border-gray-300 rounded px-2 py-1.5 w-20 text-sm"
                    data-testid="add-labor-vehicle"
                  />
                </td>
                <td className="p-1">
                  <input
                    type="text"
                    value={newEntry.fuel_expense}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, fuel_expense: e.target.value }))}
                    className="border border-gray-300 rounded px-2 py-1.5 w-20 text-sm"
                    data-testid="add-labor-fuel"
                  />
                </td>
                <td className="p-1 flex items-center gap-1">
                  <button
                    onClick={handleAddEntry}
                    className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-3 py-1.5 rounded text-xs"
                    data-testid="save-new-labor-btn"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setNewEntry({ employee: '', date: '', hours: '', vehicle: '', fuel_expense: '' });
                      setShowAddRow(false);
                    }}
                    className="bg-red-500 hover:bg-red-600 text-white px-2 py-1.5 rounded text-xs"
                    data-testid="delete-new-labor-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            )}
            {laborTimeslips.length === 0 ? (
              <tr>
                <td colSpan="6" className="p-4 text-center text-gray-500 italic">No labor entries for this project</td>
                <td className="p-2 text-center">
                  <a href={`/timeclock?project=${projectId}`} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-3 py-1.5 rounded text-xs transition-colors inline-flex items-center justify-center" data-testid="goto-timeclock-empty">
                    <Clock className="w-4 h-4" />
                  </a>
                </td>
              </tr>
            ) : (() => {
              const filtered = searchTerm.trim()
                ? laborTimeslips.filter(s => (s.employee || '').toLowerCase().includes(searchTerm.toLowerCase()))
                : laborTimeslips;
              if (filtered.length === 0) {
                return <tr><td colSpan="7" className="p-4 text-center text-gray-500 italic">No entries match "{searchTerm}"</td></tr>;
              }
              return filtered.map((slip, idx) => {
              const realIdx = laborTimeslips.indexOf(slip);
              return (
                <tr key={realIdx} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} data-testid={`labor-row-${idx}`}>
                  <td className="p-2 text-gray-700">{slip.employee}</td>
                  <td className="p-1">
                    <input
                      type="date"
                      value={toISODate(slip.date)}
                      onChange={(e) => updateLaborSlip(realIdx, 'date', fromISODate(e.target.value))}
                      className="border border-gray-300 rounded px-2 py-1.5 text-sm w-36"
                      data-testid={`labor-date-${idx}`}
                      readOnly={!canEditLabor}
                    />
                  </td>
                  <td className="p-1">
                    <input
                      type="text"
                      value={slip.hours}
                      onChange={(e) => updateLaborSlip(realIdx, 'hours', e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1.5 w-16 text-sm"
                      data-testid={`labor-hours-${idx}`}
                      readOnly={!canEditLabor}
                    />
                  </td>
                  <td className="p-2 text-gray-700"></td>
                  <td className="p-1">
                    <input
                      type="text"
                      value={slip.vehicle || ''}
                      onChange={(e) => updateLaborSlip(realIdx, 'vehicle', e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1.5 w-20 text-sm"
                      data-testid={`labor-vehicle-${idx}`}
                      readOnly={!canEditLabor}
                    />
                  </td>
                  <td className="p-1">
                    <input
                      type="text"
                      value={slip.fuel_expense || ''}
                      onChange={(e) => updateLaborSlip(realIdx, 'fuel_expense', e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1.5 w-20 text-sm"
                      data-testid={`labor-fuel-${idx}`}
                      readOnly={!canEditLabor}
                    />
                  </td>
                  <td className="p-2">
                    <a
                      href={`/timeclock?project=${projectId}${slip.slip_id ? `&edit=${slip.slip_id}` : ''}`}
                      className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-3 py-1.5 rounded text-xs transition-colors inline-flex items-center justify-center"
                      data-testid={`goto-timeclock-${idx}`}
                    >
                      <Clock className="w-4 h-4" />
                    </a>
                  </td>
                </tr>
              );
            });
            })()}
          </tbody>
          {laborTimeslips.length > 0 && (() => {
            const filtered = searchTerm.trim()
              ? laborTimeslips.filter(s => (s.employee || '').toLowerCase().includes(searchTerm.toLowerCase()))
              : laborTimeslips;
            if (filtered.length === 0) return null;
            const parseMoney = (v) => parseFloat(String(v || '0').replace(/[$,]/g, '')) || 0;
            const totals = filtered.reduce((acc, s) => ({
              hours: acc.hours + (parseFloat(s.hours) || 0),
              amount: acc.amount + slipAmount(s, laborEmpRates),
              vehicle: acc.vehicle + parseMoney(s.vehicle),
              fuel: acc.fuel + parseMoney(s.fuel_expense),
            }), { hours: 0, amount: 0, vehicle: 0, fuel: 0 });
            return (
            <tfoot>
              <tr className="border-t-2 font-medium">
                <td className="p-3 text-gray-700">Totals</td>
                <td className="p-3"></td>
                <td className="p-3 text-gray-700">{totals.hours.toFixed(2)}</td>
                <td className="p-3 text-gray-700">${totals.amount.toFixed(2)}</td>
                <td className="p-3 text-gray-700">${totals.vehicle.toFixed(2)}</td>
                <td className="p-3 text-gray-700">${totals.fuel.toFixed(2)}</td>
                <td className="p-3"></td>
              </tr>
            </tfoot>
            );
          })()}
        </table>
      </div>
      {laborTimeslips.length > 0 && (
        <div className="mt-4">
          <button
            onClick={saveLaborChanges}
            className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-6 py-2 rounded transition-colors"
            data-testid="save-labor-btn"
          >
            Save Changes
          </button>
        </div>
      )}
    </div>
  );
};

export default LaborSummaryTab;
